import { useBlocker } from "@tanstack/react-router";
import { useEffect } from "react";
import { useUnsavedChangesDialog } from "@/components/ui/unsaved-changes-prompt";

let shouldBypassPrompt = false;

export function bypassNextUnsavedChangesPrompt() {
  shouldBypassPrompt = true;
}

function consumeBypassPrompt() {
  if (!shouldBypassPrompt) return false;
  shouldBypassPrompt = false;
  return true;
}

function toStableString(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => toStableString(item)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((key) => `${key}:${toStableString(obj[key])}`).join(",")}}`;
  }

  return String(value);
}

function getRawSearch(location: unknown): string | null {
  if (!location || typeof location !== "object") return null;

  const maybeSearchStr = (location as { searchStr?: unknown }).searchStr;
  if (typeof maybeSearchStr === "string") return maybeSearchStr;

  const maybeHref = (location as { href?: unknown }).href;
  if (typeof maybeHref === "string") {
    const queryIndex = maybeHref.indexOf("?");
    if (queryIndex === -1) return "";

    const hashIndex = maybeHref.indexOf("#", queryIndex);
    return hashIndex === -1 ? maybeHref.slice(queryIndex) : maybeHref.slice(queryIndex, hashIndex);
  }

  return null;
}

export function useUnsavedChangesPrompt(isDirty: boolean) {
  const { confirmLeave } = useUnsavedChangesDialog();
  const blocker = useBlocker({
    withResolver: true,
    shouldBlockFn: ({ current, next }) => {
      if (consumeBypassPrompt()) return false;
      if (!isDirty) return false;
      if (current.pathname !== next.pathname) return true;

      const currentRawSearch = getRawSearch(current);
      const nextRawSearch = getRawSearch(next);

      if (currentRawSearch !== null && nextRawSearch !== null) {
        return currentRawSearch !== nextRawSearch;
      }
      const currentSearchString = toStableString(current.search);
      const nextSearchString = toStableString(next.search);
      return currentSearchString !== nextSearchString;
    },
    enableBeforeUnload: () => isDirty,
  });

  useEffect(() => {
    if (blocker.status !== "blocked") return;

    let cancelled = false;

    const confirmNavigation = async () => {
      const confirmed = await confirmLeave();
      if (cancelled) return;

      if (confirmed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    };

    void confirmNavigation();

    return () => {
      cancelled = true;
    };
  }, [blocker.status, confirmLeave]);
}

export function UnsavedChangesGuard({ isDirty }: { isDirty: boolean }) {
  useUnsavedChangesPrompt(isDirty);
  return null;
}
